import { axiosRubyClient as api } from '@/util/axios/axiosClient';

export interface JobWorkerStatus {
  id: number;
  worker_id: number;
  status: string;
  removal_type: string | null;
  is_late_drop: boolean;
  suspension_type: string | null;
}

export interface JobWorkerStatusesResponse {
  success: boolean;
  data: JobWorkerStatus[];
}

export interface JobQualityGateData {
  quality_gate_disabled: boolean;
  quality_gate_disabled_reason: string | null;
  quality_gate_disabled_at: string | null;
  customer_quality_gate_enabled: boolean;
}

export interface JobSupplyInfo {
  tier_applied: 'tier_1' | 'tier_2';
  low_supply: boolean;
  tier_1_passers_count: number;
  scored_worker_count: number;
  required_pool: number;
}

export type QualityGateActiveResult = {
  activeJobIds: Set<number>;
  supplyInfo: Record<number, JobSupplyInfo>;
};

export interface OvertimeWeek {
  week_start: string;
  current_hours: number;
  projected_hours: number;
  ot_hours: number;
  exceeds_40: boolean;
}

export interface OvertimeProjection {
  applicable: boolean;
  exceeds_40: boolean;
  weeks: OvertimeWeek[];
}

// Distinguishes the three outcomes of an overtime-projection lookup so the
// gate can fail CLOSED on a failed request. Collapsing all three into
// `OvertimeProjection | null` (the old shape) made a thrown/blocked request
// indistinguishable from a benign "1099 / not applicable", letting an
// admin hire a W2 worker into overtime with no confirmation whenever the
// projection call failed (network error, HTTP 500, or a blocked request).
//   - 'ok'             → applicable W2 projection; honor `exceeds_40`.
//   - 'not_applicable' → 1099 worker or backend `applicable:false`; proceed
//                        silently (no OT obligation to warn about).
//   - 'error'          → the request failed; the gate must NOT proceed
//                        silently — it surfaces the unavailable warning.
export type OvertimeProjectionResult =
  | { status: 'ok'; projection: OvertimeProjection }
  | { status: 'not_applicable' }
  | { status: 'error' };

export interface HireWorkerOptions {
  workerId: string | number;
  payRate?: number | null;
  forceHire?: boolean;
}

export interface CreateOfferOptions {
  workerId: string | number;
  payRate?: number | null;
  costRate?: number | null;
  forceInvite?: boolean;
}

export interface HireWorkerSuccess {
  success: true;
  data: {
    job: { id: number; hired_workers_count: number; status: string };
    job_worker: {
      id: number;
      job_id: number;
      worker_id: number;
      status: string;
      pay_rate: number;
      cost_rate: number;
      created_at: string | null;
    };
  };
}

export interface CreateOfferSuccess {
  success: true;
  data: {
    job_offer: {
      id: number;
      job_id: number;
      worker_id: number;
      pay_rate: number;
      cost_rate: number;
      accepted_at: string | null;
      rejected_at: string | null;
      created_at: string | null;
    };
  };
}

// Error payload from rest endpoints — always shape { success: false, errors: [CODE], message }.
export interface ApiErrorPayload {
  success: false;
  errors: string[];
  message?: string;
}

// Custom error thrown by hireWorker / createOffer when the server returns a 422.
// Carries the error code (e.g. WORKER_ALREADY_DROPPED_JOB) so callers can branch
// on the same identifiers they used with the old GraphQL mutations.
export class JobActionError extends Error {
  code: string;
  status: number;
  payload: ApiErrorPayload;

  constructor(
    code: string,
    message: string,
    status: number,
    payload: ApiErrorPayload,
  ) {
    super(message);
    this.code = code;
    this.status = status;
    this.payload = payload;
  }
}

const unwrapActionError = (err: any): never => {
  const status = err?.response?.status ?? 500;
  const payload: ApiErrorPayload | undefined = err?.response?.data;
  if (payload && payload.success === false) {
    const code = payload.errors?.[0] ?? 'SERVER_ERROR';
    const message = payload.message ?? code;
    throw new JobActionError(code, message, status, payload);
  }
  throw err;
};

export const JobsAPI = {
  getJobWorkerStatuses: async (
    jobId: number,
  ): Promise<JobWorkerStatusesResponse> => {
    const response = await api.get<JobWorkerStatusesResponse>(
      `/api/admin/jobs/${jobId}/worker_statuses`,
    );
    return response.data;
  },

  getQualityGateData: async (jobId: string): Promise<JobQualityGateData> => {
    const response = await api.get(`/api/admin/jobs/${jobId}`);
    const job = response.data.data;
    return {
      quality_gate_disabled: job.quality_gate_disabled ?? false,
      quality_gate_disabled_reason: job.quality_gate_disabled_reason ?? null,
      quality_gate_disabled_at: job.quality_gate_disabled_at ?? null,
      customer_quality_gate_enabled:
        job.account?.customer?.quality_gate_enabled ?? false,
    };
  },

  getCustomerQualityGate: async (customerId: string): Promise<boolean> => {
    const response = await api.get(`/api/admin/customers/${customerId}`);
    return response.data.data.quality_gate_enabled ?? false;
  },

  getQualityGateActiveJobIds: async (
    jobIds: number[],
  ): Promise<QualityGateActiveResult> => {
    if (jobIds.length === 0) return { activeJobIds: new Set(), supplyInfo: {} };
    const response = await api.post('/api/admin/jobs/quality_gate_active', {
      job_ids: jobIds,
    });
    const ids: number[] = response.data?.data?.active_job_ids ?? [];
    const supplyInfo: Record<number, JobSupplyInfo> =
      response.data?.data?.supply_info ?? {};
    return { activeJobIds: new Set<number>(ids), supplyInfo };
  },

  hireWorker: async (
    jobId: string | number,
    { workerId, payRate, forceHire }: HireWorkerOptions,
  ): Promise<HireWorkerSuccess> => {
    try {
      const response = await api.post<HireWorkerSuccess>(
        `/api/admin/jobs/${jobId}/hire_worker`,
        {
          worker_id: workerId,
          pay_rate: payRate ?? null,
          force_hire: forceHire ?? false,
        },
      );
      return response.data;
    } catch (err) {
      return unwrapActionError(err);
    }
  },

  createOffer: async (
    jobId: string | number,
    { workerId, payRate, costRate, forceInvite }: CreateOfferOptions,
  ): Promise<CreateOfferSuccess> => {
    try {
      const response = await api.post<CreateOfferSuccess>(
        `/api/admin/jobs/${jobId}/create_offer`,
        {
          worker_id: workerId,
          pay_rate: payRate ?? null,
          cost_rate: costRate ?? null,
          force_invite: forceInvite ?? false,
        },
      );
      return response.data;
    } catch (err) {
      return unwrapActionError(err);
    }
  },

  // Resolves the per-job overtime projection. `not_applicable` covers a 1099
  // worker or a backend `applicable:false` (no OT warning needed); `error`
  // means the request failed and the caller must fail CLOSED rather than
  // proceed silently (see OvertimeProjectionResult).
  overtimeProjection: async (
    jobId: string | number,
    workerId: string | number,
  ): Promise<OvertimeProjectionResult> => {
    try {
      const response = await api.get<{
        success: boolean;
        data: OvertimeProjection;
      }>(`/api/admin/jobs/${jobId}/overtime_projection`, {
        params: { worker_id: workerId },
      });
      const data = response.data?.data;
      if (!data || data.applicable === false)
        return { status: 'not_applicable' };
      return { status: 'ok', projection: data };
    } catch {
      return { status: 'error' };
    }
  },

  // Wizard-friendly variant — the order-creation wizard doesn't have real Job
  // ids yet, so it sends the proposed shifts directly. Same response shape
  // as overtimeProjection above.
  //
  // GRAVY-10073 break-aware: each shift entry may carry
  // `mandatory_break_minutes` (optional, defaults to 0 server-side for
  // back-compat). The backend subtracts the value from the shift's gross
  // duration so projection-OT agrees with payroll-OT (net of break).
  overtimeProjectionFromShifts: async (
    workerId: string | number,
    shifts: Array<{
      start_at: string;
      end_at: string;
      mandatory_break_minutes?: number;
    }>,
  ): Promise<OvertimeProjectionResult> => {
    if (!shifts || shifts.length === 0) return { status: 'not_applicable' };
    try {
      const response = await api.post<{
        success: boolean;
        data: OvertimeProjection;
      }>('/api/admin/overtime_projection_from_shifts', {
        worker_id: workerId,
        shifts,
      });
      const data = response.data?.data;
      if (!data || data.applicable === false)
        return { status: 'not_applicable' };
      return { status: 'ok', projection: data };
    } catch {
      return { status: 'error' };
    }
  },
};
